import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { getElementTextColor, generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme } from "../utils/themeUtils"
import { buildEventTypeUrl, buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const EventsSectionDesktop = ({ section, practice, theme, highlightedField, pageSlug = null }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showRegistration = settings.showRegistration !== false
  const showDate = settings.showDate !== false
  const showLocation = settings.showLocation !== false
  const registrationButtonText = settings.registrationButtonText || "Details"

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const sectionBackgroundColor = themeColors.bgLight || "#FFFFFF"
  const headingColor = getElementTextColor("heading", sectionBackgroundColor, theme)
  const bodyColor = getElementTextColor("body", sectionBackgroundColor, theme)

  // Get practice scheduled events (bookableEvents - camelCase)
  const allPracticeEvents = practice?.bookableEvents || []
  const selectedEventIds = settings.selectedEventIds || []

  // Filter for upcoming events only
  const upcomingEvents = allPracticeEvents.filter((event) => {
    if (!event.startsAt) return false
    return new Date(event.startsAt) > new Date()
  })

  // If specific events are selected, use those; otherwise use all upcoming events
  const selectedEvents =
    selectedEventIds.length > 0 ? upcomingEvents.filter((event) => selectedEventIds.includes(event.id)) : upcomingEvents

  // Determine if we're on the home page - use pageSlug if available, otherwise fall back to pathname
  const isHomePage =
    pageSlug === "home" ||
    pageSlug === null ||
    window.location.pathname === "/" ||
    window.location.pathname === "/home" ||
    window.location.pathname.endsWith("/home")

  // Limit based on layout and page
  let maxItems
  if (isHomePage) {
    // On home page, limit to 3 items max
    maxItems = 3
  } else {
    // On other pages, show ALL items
    maxItems = selectedEvents.length
  }

  const events = selectedEvents.slice(0, maxItems)
  const hasMoreEvents = selectedEvents.length > maxItems

  const handleEventRegistration = (event) => {
    // Navigate to the specific event page or event type page
    if (event.bookableEventTypeSlug) {
      const eventUrl = buildEventTypeUrl(event.bookableEventTypeSlug, practice)
      window.location.href = eventUrl
    } else {
      // Fallback to services page
      const servicesUrl = buildServiceUrl(null, practice)
      window.location.href = servicesUrl
    }
  }

  const formatEventDate = (startsAt, endsAt, timeZone) => {
    if (!startsAt) return ""
    const eventDate = new Date(startsAt)
    const formattedDate = eventDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    })
    const formattedTime = eventDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone
    })
    return `${formattedDate} at ${formattedTime}`
  }

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  // List layout - horizontal cards with image on left, content on right
  const renderEventList = (event) => (
    <div key={event.id} className="relative flex items-center gap-[72px] border border-[#e6dad7] bg-white p-10">
      <div className="flex flex-1 items-center gap-8">
        <div className="shrink-0">
          {event.photoUrl ? (
            <img
              src={event.photoUrl}
              alt={event.name}
              className="h-48 w-48 rounded bg-center bg-no-repeat object-cover"
              style={{ backgroundSize: "155.84% 158.4%" }}
            />
          ) : (
            <div className="flex h-48 w-48 items-center justify-center rounded bg-gray-200">
              <span className="text-gray-400">No image</span>
            </div>
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div>
            {event.amount_cents && (
              <p
                className="text-[20px] font-semibold leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: bodyColor
                }}>
                {formatPrice(event.amount_cents, event.amount_currency || "USD")}
              </p>
            )}
            <h3
              className="text-[28px] font-bold leading-[40px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                color: headingColor,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}>
              {event.name}
            </h3>
          </div>

          {showDate && event.starts_at && (
            <p
              className="whitespace-pre text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: bodyColor
              }}>
              {formatEventDate(event.starts_at, event.ends_at, event.time_zone)}
              {showLocation && event.location && ` • ${event.location}`}
            </p>
          )}

          <div
            className="prose prose-lg max-w-none overflow-hidden text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: "Work Sans, sans-serif",
              color: bodyColor,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical"
            }}
            dangerouslySetInnerHTML={{ __html: event.description }}
          />
        </div>
      </div>

      {showRegistration && (
        <div className="shrink-0">
          <button
            onClick={() => handleEventRegistration(event)}
            className="px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                borderRadius: theme?.borderRadius || "8px"
              },
              theme
            )}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
            }}>
            {registrationButtonText}
          </button>
        </div>
      )}
    </div>
  )

  // Card layout for 2 and 3 highlights
  const renderEventCard = (event) => (
    <div key={event.id} className="flex flex-col gap-4">
      <div className="aspect-[396/250] w-full">
        {event.photoUrl ? (
          <img
            src={event.photoUrl}
            alt={event.name}
            className="h-full w-full bg-center bg-no-repeat object-cover"
            style={{ backgroundSize: "100% 158.4%" }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-200">
            <span className="text-gray-400">No image</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div>
          {event.amount_cents && (
            <p
              className="text-[20px] font-semibold leading-[32px]"
              style={{
                fontFamily: "Work Sans, sans-serif",
                color: bodyColor
              }}>
              {formatPrice(event.amount_cents, event.amount_currency || "USD")}
            </p>
          )}
          <h3
            className="w-[360px] text-[28px] font-bold leading-[40px]"
            style={{
              fontFamily: "Playfair Display, serif",
              color: headingColor
            }}>
            {event.name}
          </h3>
        </div>

        {showDate && event.starts_at && (
          <p
            className="min-w-full text-[16px] font-normal leading-[24px]"
            style={{
              fontFamily: "Work Sans, sans-serif",
              width: "min-content",
              color: bodyColor
            }}>
            {formatEventDate(event.starts_at, event.ends_at, event.time_zone)}
            {showLocation && event.location && ` • ${event.location}`}
          </p>
        )}

        <div
          className="prose prose-lg min-w-full max-w-none overflow-hidden text-[18px] font-normal leading-[32px]"
          style={{
            fontFamily: "Work Sans, sans-serif",
            color: bodyColor,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            width: "min-content"
          }}
          dangerouslySetInnerHTML={{ __html: event.description }}
        />
      </div>

      {showRegistration && (
        <button
          onClick={() => handleEventRegistration(event)}
          className="self-start px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg"
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              borderRadius: theme?.borderRadius || "8px"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {registrationButtonText}
        </button>
      )}
    </div>
  )

  const renderContent = () => (
    <div className="mx-auto max-w-[1160px]">
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-8">
          {/* Title and Browse Services Button */}
          <div className="flex w-full items-center justify-between gap-10">
            <div className="flex flex-col items-start gap-4">
              <h2
                className="text-left text-[40px] font-bold leading-[48px]"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: headingColor
                }}>
                {section.headline || "Events"}
              </h2>
              {section.body && (
                <p
                  className="max-w-3xl text-left text-xl leading-relaxed"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor
                  }}>
                  {section.body}
                </p>
              )}
            </div>

            {/* View All Events Button - Only shown if there are more events on home page */}
            {hasMoreEvents && isHomePage && (
              <button
                onClick={() => {
                  const servicesUrl = buildServiceUrl(null, practice, { anchor: "events-section-full" })
                  window.location.href = servicesUrl
                }}
                className="px-6 py-2 text-sm font-medium text-white transition-colors"
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    borderRadius: theme?.borderRadius || "12px"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                }}>
                View All Events
              </button>
            )}
          </div>

          {events.length > 0 ? (
            <div className="flex flex-col gap-6">
              {layout === "list" && <div className="flex flex-col gap-6">{events.map(renderEventList)}</div>}

              {layout === "2_highlights" && (
                <div className="flex flex-col gap-[72px]">
                  <div className="flex gap-10">
                    <div className="max-w-[560px] flex-1">{events.slice(0, 1).map(renderEventCard)}</div>
                  </div>
                </div>
              )}

              {layout === "3_highlights" && (
                <div className="flex flex-col gap-[72px]">
                  <div className="flex gap-10">
                    {events.slice(0, 3).map((event, index) => (
                      <div key={event.id || index} className="w-[360px] max-w-[560px]">
                        {renderEventCard(event)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p style={{ color: bodyColor }}>No upcoming events found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const sectionStyle = {
    backgroundColor: sectionBackgroundColor
  }

  return (
    <section
      id="events-section-full"
      className={twMerge("scroll-mt-24 py-14 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={sectionStyle}>
      <AnimatedSection>{renderContent()}</AnimatedSection>
    </section>
  )
}

export default EventsSectionDesktop
